import { Project, WorkingPeriod } from "../project/typesDefinition";
import { Order, OrderLine, RecurringOrder, TimeUnit } from "./typesDefinition";
import dayjs, { Dayjs } from "dayjs";
import { TimesheetsAsLinkedResource } from "@/helpers/typesDefinition";

export function isOrder(order: unknown): order is Order {
  return (order as Order).__typename === "Order";
}

export function orderDefaultTitle(
  project: Project,
  orderInTitle: string,
  nextOrderNumber: number,
): string {
  return `${project.client.name} - ${project.name} - ${orderInTitle} ${nextOrderNumber}`;
}

export const formatMonthDate = (date: Dayjs): string => {
  return date.format("MMMM YY");
};

export const formatMonthDateShort = (date: Dayjs): string => {
  return date.format("MMM YY");
};

export const createPeriodFormatter =
  (formatter: (date: Dayjs) => string) =>
  (
    period: Order | RecurringOrder | WorkingPeriod | TimesheetsAsLinkedResource,
  ): string => {
    console.log("Start " + period.startDate);
    console.log("End " + period.endDate);
    if (isSingleMonth(period)) return formatter(dayjs(period.startDate));
    return `${formatter(dayjs(period.startDate))} - ${formatter(
      dayjs(period.endDate).subtract(1, "day"),
    )}`;
  };

export const formatPeriodDate = createPeriodFormatter(formatMonthDate);

export const formatPeriodDateShort =
  createPeriodFormatter(formatMonthDateShort);

export const paymentOptions = {
  post_paid: "Post-paid",
  pre_paid: "Retainer",
} as { [key: string]: string };

export const budgetTracking = {
  monthly: "Monthly",
  global: "Global",
} as { [key: string]: string };

export const plannedTimeTracking = {
  order_amount: "Order amount",
  global_budget: "Global budget",
  invoiced_amount: "Invoiced amount",
} as { [key: string]: string };

export const estimationUnits = {
  hours: "Hours",
  days: "Days",
} as { [key: string]: string };

export const legalNotices = {
  post_paid: `By confirming this order, you acknowledge that this order is irrevocable.
  At the end of each month, 26lights will calculate the Time Logged by the consultants and issue an Invoice. This Invoice is payable ten (10) days from date of invoice.
  The client acknowledges that the Time logged can be different from the Time Ordered, consequence on one hand of the illness of the consultant’s holidays, emergency on another project and on the other hand of new clients requests orally or by email or because the work to be performed requires more time than expected.
  Any amount not paid within the contractually agreed time limits, which may be extended by mutual agreement between the Parties, will bear interest at three (3) times the statutory interest rate until payment of that sum unpaid. 26lights reserves also the right to suspend the work of its consultants.`,
  pre_paid: `By confirming this order, you acknowledge that this order is irrevocable. A Retainer Invoice will be sent by email with the amount detailed in this Order. This Retainer Invoice is payable ten (10) days from date of invoice.
  At the end of each month, 26lights will calculate the Time Delta between the Time Ordered and the Time Logged by the consultants. If the Time Logged is superior to the Time Ordered, a Delta Invoice will be issued. If the Time Logged is inferior a Credit Note will be issued. This Delta Invoice is payable ten (10) days from date of invoice.
  The client acknowledges that the Time Delta can be the consequence on one hand of the illness of the consultant’s holidays, emergency on another project and on the other hand of new clients requests orally or by email or because the work to be performed requires more time than expected.
  Any amount not paid within the contractually agreed time limits, which may be extended by mutual agreement between the Parties, will bear interest at three (3) times the statutory interest rate until payment of that sum unpaid. 26lights reserves also the right to suspend the work of its consultants.`,
} as { [key: string]: string };

export const sortedStatusLabels = [
  "draft",
  "to_validate",
  "late_approval",
  "approved",
  "retainer_sent",
  "retainer_reminder_1",
  "retainer_reminder_2",
  "retainer_paid",
  "performed",
  "postpaid_draft",
  "postpaid_invoiced",
  "postpaid_reminder_1",
  "postpaid_reminder_2",
  "postpaid_reminder_3",
  "closed",
  "rejected",
  "late_payment",
] as const;

export type StatusLabelsKey = (typeof sortedStatusLabels)[number];

export function transformQuantitiesToDisplay(
  quantities: number[],
  units: TimeUnit[],
): number[] {
  return quantities.map((quantity, index) => {
    const unit = units[index];
    return transformQuantityToDisplay(quantity, unit);
  });
}
export function transformQuantityToDisplay(
  quantity: number,
  unit: TimeUnit,
): number {
  switch (unit) {
    case TimeUnit.HOUR:
      return quantity / 60;
    case TimeUnit.DAY:
      return quantity / 480;
    default:
      throw new Error("Unit should be either hours or days");
  }
}

export function transformQuantityToRaw(
  quantity: number,
  unit: TimeUnit,
): number {
  switch (unit) {
    case TimeUnit.HOUR:
      return quantity * 60;
    case TimeUnit.DAY:
      return quantity * 480;
    default:
      throw new Error("Unit should be either hours or days");
  }
}

export function transformOrderLineToDisplay(orderLine: OrderLine): OrderLine {
  return {
    ...orderLine,
    quantity: transformQuantityToDisplay(orderLine.quantity, orderLine.unit),
  };
}

export function isSingleMonth(
  order: Order | RecurringOrder | WorkingPeriod | TimesheetsAsLinkedResource,
): boolean {
  return dayjs(order.startDate).isSame(
    dayjs(order.endDate).subtract(1, "day"),
    "month",
  );
}
